import { Box, Divider, List, ListItem, ListItemText, Paper, Stack, Typography } from '@mui/material'
import { RobotApi } from 'api/RobotApi'
import RobotDiagnosticNotification from 'components/Robot/RobotDiagnosticNotification'
import useRobot from 'hooks/useRobot'
import { useNotificationContext } from 'index'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useDiagnosticStore from 'store/diagnostic'
import WebsocketManager, { SOCKET_MESSAGES } from 'utils/WebsocketManager'
import { GLOBAL_WS_PATH, resolveDiagnosticNotification } from 'utils/diagnostic'
import { IDiagnosticHistory } from 'utils/interfaces'

/** Максимальное время ожидания ответа сервера на резолв, мс. */
const RESOLVE_TIMEOUT = 10000

const RobotDiagnostics = () => {
  const { t } = useTranslation()
  const { addNotification } = useNotificationContext()
  const { notifications, removeNotification } = useDiagnosticStore()
  const { data: robot } = useRobot()

  const [history, setHistory] = useState<IDiagnosticHistory[]>([])
  const [pendingAction, setPendingAction] = useState<{ notifId: string; buttonId: string } | null>(null)
  const pendingActionRef = useRef(pendingAction)
  pendingActionRef.current = pendingAction
  const robotId = robot?.id

  // Загрузка и периодическое обновление истории раз в 10 секунд.
  useEffect(() => {
    if (!robotId) return

    const fetchHistory = () => {
      RobotApi.fetchDiagnosticHistory(robotId)
        .then(setHistory)
        .catch(() => {
          /* сокрытие фоновых ошибок опроса истории */
        })
    }

    fetchHistory()
    const interval = setInterval(fetchHistory, 10000)
    return () => clearInterval(interval)
  }, [robotId])

  const finishPending = (notifId: string, showSuccess: boolean) => {
    if (showSuccess) {
      addNotification({ message: t('diagnostics.resolveSuccess'), severity: 'success' })
    }
    RobotApi.fetchDiagnosticHistory(robotId as number).then(setHistory)
    removeNotification(notifId)
    setPendingAction(null)
  }

  // Подписка на ответ сервера о резолве уведомления.
  useEffect(() => {
    WebsocketManager.init(GLOBAL_WS_PATH)

    const handleResponse = () => {
      const pending = pendingActionRef.current
      if (!pending) return
      finishPending(pending.notifId, true)
    }

    WebsocketManager.addEventListener(SOCKET_MESSAGES.DIAGNOSTIC_RESPONSE, handleResponse, GLOBAL_WS_PATH)
    return () => {
      WebsocketManager.removeEventListener(SOCKET_MESSAGES.DIAGNOSTIC_RESPONSE, handleResponse, GLOBAL_WS_PATH)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [robotId, addNotification, removeNotification, t])

  // Фолбэк: если сервер не ответил вовремя, закрываем pending, чтобы UI не завис.
  useEffect(() => {
    if (!pendingAction) return

    const timer = setTimeout(() => {
      addNotification({ message: t('diagnostics.resolveError'), severity: 'warning' })
      setPendingAction(null)
    }, RESOLVE_TIMEOUT)

    return () => clearTimeout(timer)
  }, [pendingAction, addNotification, t])

  const handleAction = async (notifId: string, buttonId: string) => {
    if (!robot?.id || pendingAction) return

    const notif = notifications.find(n => n.notification_id === notifId)
    if (!notif) return

    setPendingAction({ notifId, buttonId })
    resolveDiagnosticNotification(notif, buttonId)
  }

  const getResolvedByLabel = (code: number) => {
    switch (code) {
      case 1:
        return t('diagnostics.resolvedBy.diagnostic')
      case 2:
        return t('diagnostics.resolvedBy.fms')
      case 3:
        return t('diagnostics.resolvedBy.nodeRed')
      default:
        return t('diagnostics.resolvedBy.active')
    }
  }

  return (
    <Box height="100%" display="flex" flexDirection="column" overflow="hidden">
      {/* Active Alerts — верхняя половина */}
      <Box flex="1 1 50%" minHeight={0} display="flex" flexDirection="column" p={1} pb={0}>
        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
          {t('diagnostics.activeNotifications')}
        </Typography>
        <Box flex={1} minHeight={0} sx={{ overflowY: 'auto' }} pr={1}>
          <Stack spacing={0.5}>
            {notifications.filter(n => n.robot_id === robot?.id).length > 0 ? (
              notifications
                .filter(n => n.robot_id === robot?.id)
                .map(n => (
                  <RobotDiagnosticNotification
                    key={n.notification_id}
                    notification={n}
                    onAction={btnId => handleAction(n.notification_id, btnId)}
                    loadingButtonId={pendingAction?.notifId === n.notification_id ? pendingAction.buttonId : undefined}
                  />
                ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t('diagnostics.noActiveNotifications')}
              </Typography>
            )}
          </Stack>
        </Box>
      </Box>

      <Divider sx={{ my: 0.75 }} />

      {/* Diagnostics History — нижняя половина */}
      <Box flex="1 1 50%" minHeight={0} display="flex" flexDirection="column" p={1} pt={0}>
        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
          {t('diagnostics.history')}
        </Typography>
        <Paper variant="outlined" sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          <List dense disablePadding>
            {history.map((item, index) => (
              <ListItem key={item.id} divider={index !== history.length - 1} disableGutters sx={{ px: 1, py: 0.25 }}>
                <ListItemText
                  sx={{ my: 0 }}
                  primary={item.message}
                  primaryTypographyProps={{ variant: 'body2', noWrap: true, sx: { fontSize: '0.8125rem' } }}
                  secondary={t('diagnostics.historyAction', {
                    action: item.action,
                    date: new Date(item.timestamp).toLocaleString(),
                  })}
                  secondaryTypographyProps={{ variant: 'caption', sx: { fontSize: '0.6875rem' } }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    ml: 1,
                    whiteSpace: 'nowrap',
                    fontSize: '0.6875rem',
                    color: item.resolvedBy === 0 ? 'warning.main' : 'primary.main',
                  }}
                >
                  {getResolvedByLabel(item.resolvedBy)}
                </Typography>
              </ListItem>
            ))}
            {history.length === 0 && (
              <ListItem sx={{ px: 1, py: 0.25 }}>
                <ListItemText
                  primary={t('diagnostics.historyEmpty')}
                  primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                />
              </ListItem>
            )}
          </List>
        </Paper>
      </Box>
    </Box>
  )
}

export default RobotDiagnostics
